import { injectTyped } from '@/inject'
import { Chart } from '@/models/chart.model'
import { ChartDetails } from '@/models/dashboard.model'
import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
import { ServiceKeys } from '@/services/symbols'
import { useAsyncState } from '@vueuse/core'
import { computed, type MaybeRefOrGetter, toValue, watchEffect } from 'vue'

export function useGivenChartDetails(chartDetails: MaybeRefOrGetter<ChartDetails | undefined>) {
  const resolvedChartDetails = computed(() => toValue(chartDetails))

  const headers = computed((): AnalyzeTableHeader[] => {
    const data = resolvedChartDetails.value
    if (!data || !Array.isArray(data.columns)) return []

    const chartHeaders: AnalyzeTableHeader[] = []
    data.columns.forEach((currentColumn) => {
      const header: AnalyzeTableHeader = {
        title: currentColumn.titleDefault || currentColumn.id,
        // Vue.js item slots are more reliable with simple key names.
        key: currentColumn.id.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        sortable: true,
      }

      if (!chartHeaders.find((currentHeader) => currentHeader.key === header.key)) {
        chartHeaders.push(header)
      }
    })

    return chartHeaders
  })

  const items = computed((): AnalyzeTableItem<unknown>[] | any[] => {
    const data = resolvedChartDetails.value
    if (!data || !Array.isArray(data.rows)) return []

    return data.rows.map((currentRow) => {
      const cellMappings = currentRow.cells.reduce<
        Array<{ header: string; value: string; artifactId?: string }>
      >((result, cell, index) => {
        const header = headers.value[index]?.key
        if (!header) return result

        result.push({
          header: String(header),
          value: cell.value,
          artifactId: cell.artifactId ? cell.artifactId.toString() : undefined,
        })

        return result
      }, [])

      const rowValues = Object.fromEntries(cellMappings.map((cell) => [cell.header, cell.value]))
      const firstArtifactId =
        cellMappings.find((cell) => Boolean(cell.artifactId))?.artifactId ??
        currentRow.cells.find((cell) => cell.artifactId)?.artifactId?.toString() ??
        ''

      return {
        ...rowValues,
        id: firstArtifactId,
      }
    })
  })

  const links = computed((): Record<number, Record<string, string>> => {
    const data = resolvedChartDetails.value
    if (!data || !Array.isArray(data.rows)) return {}

    return Object.fromEntries(
      data.rows.map((currentRow, rowIndex) => {
        const rowLinks = Object.fromEntries(
          currentRow.cells
            .map((cell, index) => {
              const headerKey = headers.value[index]?.key
              if (!headerKey || !cell.artifactId) return undefined

              return [headerKey, cell.artifactId.toString()] as const
            })
            .filter((entry): entry is readonly [string, string] => Boolean(entry)),
        )

        return [rowIndex, rowLinks]
      }),
    )
  })

  return {
    headers,
    items,
    links,
    detailsHeaders: headers,
    detailsItems: items,
    detailsLinks: links,
  }
}

export function useChartDetails(
  chart: MaybeRefOrGetter<Chart | undefined>,
  currentSeriesName: MaybeRefOrGetter<string | undefined>,
  serverOptions: MaybeRefOrGetter<{ page: number; rowsPerPage: number }>,
  groupByColumnId: MaybeRefOrGetter<string> = '',
) {
  const dashboardService = injectTyped(ServiceKeys.dashboard)

  const {
    state: chartDetails,
    isLoading: loading,
    execute,
  } = useAsyncState(
    async () => {
      const resolvedChart = toValue(chart)
      const resolvedSeriesName = toValue(currentSeriesName)
      if (!resolvedChart || !resolvedSeriesName) return undefined

      return dashboardService.getChartDetailsData(
        resolvedChart,
        resolvedSeriesName,
        toValue(groupByColumnId),
        toValue(serverOptions),
      )
    },
    undefined as ChartDetails | undefined,
    {
      immediate: false,
      resetOnExecute: true,
    },
  )

  watchEffect(() => {
    const resolvedChart = toValue(chart)
    const resolvedSeriesName = toValue(currentSeriesName)
    const resolvedServerOptions = toValue(serverOptions)
    // Touch groupByColumnId to track as dependency
    void toValue(groupByColumnId)

    if (!resolvedChart || !resolvedSeriesName) {
      chartDetails.value = undefined
      return
    }

    void resolvedServerOptions.page
    void resolvedServerOptions.rowsPerPage
    void execute(0)
  })

  return {
    ...useGivenChartDetails(chartDetails),
    loading,
  }
}
